import React, { useId } from 'react';

const SHAPE_LABELS = {
  circle: 'daire',
  oval: 'oval',
  triangle: 'üçgen',
  square: 'kare',
  rounded_square: 'yuvarlak köşeli kare',
  rectangle: 'dikdörtgen',
  trapezoid: 'yamuk',
  parallelogram: 'paralelkenar',
  pentagon: 'beşgen',
  hexagon: 'altıgen',
  octagon: 'sekizgen',
  star_5: 'beş köşeli yıldız',
  star_6: 'altı köşeli yıldız',
  heart: 'kalp',
  cross: 'artı',
  arrow: 'ok',
  crescent: 'hilal',
  semicircle: 'yarım daire',
  kite: 'uçurtma',
  shield: 'kalkan',
};

const PATTERN_LABELS = {
  empty: 'boş',
  solid: 'dolu',
  dots_sparse: 'noktalı',
  dots_dense: 'sık noktalı',
  horizontal_lines: 'yatay çizgili',
  vertical_lines: 'dikey çizgili',
  diagonal_lines: 'çapraz çizgili',
  crosshatch: 'çapraz taralı',
  waves: 'dalgalı',
  checker: 'kareli',
};

export function oddOneOutShapeLabel(shapeKey, patternKey) {
  const shape = SHAPE_LABELS[shapeKey] || 'şekil';
  const pattern = PATTERN_LABELS[patternKey] || '';
  return `${pattern} ${shape}`.trim();
}

function PatternDefinition({ patternKey, id }) {
  const stroke = '#6d45df';
  const background = '#f5f3ff';
  if (patternKey === 'dots_sparse' || patternKey === 'dots_dense') {
    const dense = patternKey === 'dots_dense';
    const size = dense ? 10 : 18;
    return (
      <pattern id={id} width={size} height={size} patternUnits="userSpaceOnUse">
        <rect width={size} height={size} fill={background} />
        <circle cx={size / 2} cy={size / 2} r={dense ? 2.2 : 3.2} fill={stroke} />
      </pattern>
    );
  }
  if (patternKey === 'horizontal_lines' || patternKey === 'vertical_lines') {
    const vertical = patternKey === 'vertical_lines';
    return (
      <pattern id={id} width="14" height="14" patternUnits="userSpaceOnUse">
        <rect width="14" height="14" fill={background} />
        {vertical
          ? <path d="M 3 0 V 14 M 11 0 V 14" stroke={stroke} strokeWidth="2.4" />
          : <path d="M 0 3 H 14 M 0 11 H 14" stroke={stroke} strokeWidth="2.4" />}
      </pattern>
    );
  }
  if (patternKey === 'diagonal_lines' || patternKey === 'crosshatch') {
    return (
      <pattern id={id} width="14" height="14" patternUnits="userSpaceOnUse">
        <rect width="14" height="14" fill={background} />
        <path d="M -4 4 L 4 -4 M 0 14 L 14 0 M 10 18 L 18 10" stroke={stroke} strokeWidth="2.2" />
        {patternKey === 'crosshatch' && (
          <path d="M 10 -4 L 18 4 M 0 0 L 14 14 M -4 10 L 4 18" stroke={stroke} strokeWidth="2.2" />
        )}
      </pattern>
    );
  }
  if (patternKey === 'waves') {
    return (
      <pattern id={id} width="22" height="14" patternUnits="userSpaceOnUse">
        <rect width="22" height="14" fill={background} />
        <path d="M 0 7 Q 5.5 1 11 7 T 22 7" fill="none" stroke={stroke} strokeWidth="2.5" />
      </pattern>
    );
  }
  if (patternKey === 'checker') {
    return (
      <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
        <rect width="16" height="16" fill={background} />
        <rect width="8" height="8" fill={stroke} />
        <rect x="8" y="8" width="8" height="8" fill={stroke} />
      </pattern>
    );
  }
  return null;
}

function ShapeGeometry({ shapeKey, fill, stroke }) {
  const common = { fill, stroke, strokeWidth: 5, strokeLinejoin: 'round' };
  switch (shapeKey) {
    case 'circle': return <circle cx="50" cy="50" r="34" {...common} />;
    case 'oval': return <ellipse cx="50" cy="50" rx="39" ry="28" {...common} />;
    case 'triangle': return <polygon points="50,10 90,84 10,84" {...common} />;
    case 'square': return <rect x="16" y="16" width="68" height="68" {...common} />;
    case 'rounded_square': return <rect x="15" y="15" width="70" height="70" rx="16" {...common} />;
    case 'rectangle': return <rect x="9" y="24" width="82" height="52" rx="4" {...common} />;
    case 'trapezoid': return <polygon points="28,18 72,18 91,82 9,82" {...common} />;
    case 'parallelogram': return <polygon points="29,18 91,18 71,82 9,82" {...common} />;
    case 'pentagon': return <polygon points="50,8 91,38 75,87 25,87 9,38" {...common} />;
    case 'hexagon': return <polygon points="27,10 73,10 94,50 73,90 27,90 6,50" {...common} />;
    case 'octagon': return <polygon points="31,8 69,8 92,31 92,69 69,92 31,92 8,69 8,31" {...common} />;
    case 'star_5': return <polygon points="50,6 61,36 94,37 68,57 77,90 50,71 23,90 32,57 6,37 39,36" {...common} />;
    case 'star_6': return <path d="M50 6 L62 34 L92 28 L72 52 L92 76 L62 70 L50 94 L38 70 L8 76 L28 52 L8 28 L38 34 Z" {...common} />;
    case 'heart': return <path d="M50 88 C39 77 12 59 12 35 C12 17 34 9 50 27 C66 9 88 17 88 35 C88 59 61 77 50 88 Z" {...common} />;
    case 'cross': return <path d="M37 8 H63 V37 H92 V63 H63 V92 H37 V63 H8 V37 H37 Z" {...common} />;
    case 'arrow': return <path d="M8 38 H55 V15 L92 50 L55 85 V62 H8 Z" {...common} />;
    case 'crescent': return <path d="M72 12 C39 16 23 37 27 60 C31 82 52 94 77 85 C58 81 47 68 47 51 C47 34 56 20 72 12 Z" {...common} />;
    case 'semicircle': return <path d="M10 74 A40 40 0 0 1 90 74 Z" {...common} />;
    case 'kite': return <polygon points="50,6 82,43 50,94 18,43" {...common} />;
    case 'shield': return <path d="M14 14 H86 V50 C86 72 69 87 50 94 C31 87 14 72 14 50 Z" {...common} />;
    default: return <circle cx="50" cy="50" r="34" {...common} />;
  }
}

export default function OddOneOutShape({ shapeKey, patternKey, rotationDeg = 0 }) {
  const rawId = useId();
  const patternId = `odd-pattern-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const fill = patternKey === 'solid'
    ? '#6d45df'
    : patternKey === 'empty'
      ? '#f5f3ff'
      : `url(#${patternId})`;
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true" focusable="false">
      <defs>
        <PatternDefinition patternKey={patternKey} id={patternId} />
      </defs>
      <g transform={`rotate(${Number(rotationDeg || 0)} 50 50)`}>
        <ShapeGeometry shapeKey={shapeKey} fill={fill} stroke="#4c1d95" />
      </g>
    </svg>
  );
}
